import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { NOTIFICATION_TOPICS, NOTIFICATION_CHANNELS, CHANNEL_LABELS } from '../../context/enums';
import { useAuth } from '../../context/AuthContext';
import userService from '../../services/userService';
import fireDepartmentService from '../../services/fireDepartmentService';
import API_CONFIG from '../../config/apiConfig';
import {
  toUserUpdatePayload,
  toNotificationPreferencesPatch,
  toProfileState,
  resolveSelectedFireDeptId,
  mergeNotificationPreferences,
  extractErrorMessage,
} from '../../utils/profileMapper';
import FireDepartmentSelector from './FireDepartmentSelector';
import './Profile.css';

/** Fallback error message shown when loading profile data fails. */
const LOAD_ERROR_FALLBACK = 'Failed to load profile data';

/** Fallback error message shown when saving profile changes fails. */
const SAVE_ERROR_FALLBACK = 'Failed to save profile changes';

/** Success message shown after profile changes are saved. */
const SAVE_SUCCESS_MESSAGE = 'Profile updated successfully';

/** Label for the save button while idle. */
const SAVE_BUTTON_LABEL = 'Save Changes';

/** Label for the save button while a save is in progress. */
const SAVING_BUTTON_LABEL = 'Saving...';

/** Placeholder text for each editable profile input field. */
const PLACEHOLDERS = {
  firstName: 'Enter your first name',
  lastName: 'Enter your last name',
  phoneNumber: 'Enter your phone number',
  streetAddr: 'Enter your street address',
  street2Addr: 'Apartment, suite, etc. (optional)',
  cityAddr: 'Enter your city',
  stateProvinceAddr: 'Enter your state or province',
  postalCodeAddr: 'Enter your postal code',
  countryAddr: 'Enter your country',
};

/**
 * Default notification preference rows shown in the Notification Settings table.
 */
const DEFAULT_NOTIFICATION_PREFERENCES = [
  {
    topic: NOTIFICATION_TOPICS.FIRE_CONTROLLER_ACTIVATION,
    label: 'Smart Controller Activation',
    description: 'Notifies you when your fire suppression system activates',
    emailEnabled: false,
    smsEnabled: false,
    pushEnabled: false,
  },
  {
    topic: NOTIFICATION_TOPICS.FIRE_CONTROLLER_CONNECTION_LOSS,
    label: 'Smart Controller Connection Loss',
    description: 'Notifies you if your Smart Controller loses connection',
    emailEnabled: false,
    smsEnabled: false,
    pushEnabled: false,
  },
  {
    topic: NOTIFICATION_TOPICS.NEIGHBOR_SYSTEM_ACTIVATION,
    label: 'Collaborative System Activation',
    description: 'Notifies you when a neighboring system in your area activates',
    emailEnabled: false,
    smsEnabled: false,
    pushEnabled: false,
  },
];

/**
 * Address field configuration, mapped to render each address input.
 */
const ADDRESS_FIELDS = [
  { key: 'streetAddr', label: 'Street Address', placeholder: PLACEHOLDERS.streetAddr },
  { key: 'street2Addr', label: 'Street Address Line 2', placeholder: PLACEHOLDERS.street2Addr },
  { key: 'cityAddr', label: 'City', placeholder: PLACEHOLDERS.cityAddr },
  { key: 'stateProvinceAddr', label: 'State / Province', placeholder: PLACEHOLDERS.stateProvinceAddr },
  { key: 'postalCodeAddr', label: 'Postal Code', placeholder: PLACEHOLDERS.postalCodeAddr },
  { key: 'countryAddr', label: 'Country', placeholder: PLACEHOLDERS.countryAddr },
];

/**
 * Profile component - User profile and notification preferences management interface
 * @returns {JSX.Element} The rendered profile page with user information and notification settings
 */
const Profile = () => {
  const navigate = useNavigate();
  const { user, getToken } = useAuth();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: {
      streetAddr: '',
      street2Addr: '',
      cityAddr: '',
      stateProvinceAddr: '',
      postalCodeAddr: '',
      countryAddr: '',
    },
  });
  const [notificationPreferences, setNotificationPreferences] = useState(
    DEFAULT_NOTIFICATION_PREFERENCES
  );
  const [fireDepartments, setFireDepartments] = useState([]);
  const [selectedFireDeptId, setSelectedFireDeptId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    const userId = user?.sub;
    if (!userId) {
      return;
    }

    /**
     * Loads the user's profile and notification preferences from Fire Bridge.
     * Runs on mount and whenever the authenticated user or token changes.
     * @returns {Promise<void>}
     */
    const loadProfileData = async () => {
      try {
        userService.initialize(API_CONFIG.BASE_URL, getToken);
        fireDepartmentService.initialize(API_CONFIG.BASE_URL, getToken);

        const [userResponse, notificationResponse, fireDepartmentsResponse] = await Promise.all([
          userService.getUser(userId),
          userService.getUserNotificationPreferences(userId),
          fireDepartmentService.getAllFireDepartments(),
        ]);

        const profileState = toProfileState(userResponse);
        setProfile(profileState);
        setNotificationPreferences(
          mergeNotificationPreferences(DEFAULT_NOTIFICATION_PREFERENCES, notificationResponse.preferences)
        );
        setFireDepartments(fireDepartmentsResponse);
        setSelectedFireDeptId(
          resolveSelectedFireDeptId(profileState.fireDeptId, fireDepartmentsResponse)
        );
      } catch (error) {
        setPageError(extractErrorMessage(error, LOAD_ERROR_FALLBACK));
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [user, getToken]);

  /**
   * Handles navigation back to the dashboard
   * @returns {void}
   */
  const handleBack = () => {
    navigate('/dashboard');
  };

  /**
   * Handles changing a top-level profile field
   * @param {string} field - The profile field to update
   * @param {string} value - The new value for the field
   * @returns {void}
   */
  const handleProfileFieldChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Handles changing an address field
   * @param {string} field - The address field to update
   * @param {string} value - The new value for the field
   * @returns {void}
   */
  const handleAddressFieldChange = (field, value) => {
    setProfile((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }));
  };

  /**
   * Toggles a notification channel for a given topic
   * @param {string} topic - The notification topic to update
   * @param {string} channel - The notification channel to toggle
   * @returns {void}
   */
  const handleNotificationToggle = (topic, channel) => {
    setNotificationPreferences((prev) =>
      prev.map((preference) =>
        preference.topic === topic
          ? { ...preference, [channel]: !preference[channel] }
          : preference
      )
    );
  };

  /**
   * Handles form submission for profile updates. Sends profile and notification
   * preference updates in parallel; both are sent regardless of the other's outcome.
   * @param {Event} e - The form submit event
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const userId = user?.sub;
    if (!userId) {
      setPageError(SAVE_ERROR_FALLBACK);
      return;
    }

    setIsSaving(true);
    setPageError(null);
    setSuccessMessage(null);

    const [userResult, notificationResult] = await Promise.allSettled([
      userService.updateUser(userId, toUserUpdatePayload(profile, selectedFireDeptId)),
      userService.updateUserNotificationPreferences(userId, toNotificationPreferencesPatch(notificationPreferences)),
    ]);

    const errors = [userResult, notificationResult]
      .filter((result) => result.status === 'rejected')
      .map((result) => extractErrorMessage(result.reason, SAVE_ERROR_FALLBACK));
    const uniqueErrors = [...new Set(errors)];

    if (uniqueErrors.length > 0) {
      setPageError(uniqueErrors.join('; '));
    } else {
      setSuccessMessage(SAVE_SUCCESS_MESSAGE);
    }

    setIsSaving(false);
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <button className="back-button" onClick={handleBack} aria-label="Back">
          <FaArrowLeft />
        </button>
        <h1>Profile</h1>
      </div>

      {pageError && <div className="profile-error-banner">{pageError}</div>}
      {successMessage && <div className="profile-success-banner">{successMessage}</div>}

      {isLoading ? (
        <div className="profile-loading">
          <div className="profile-loading-spinner"></div>
        </div>
      ) : (
        <div className="profile-form-wrapper">
          {isSaving && (
            <div className="profile-loading profile-loading-overlay">
              <div className="profile-loading-spinner"></div>
            </div>
          )}
          <form className="profile-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                value={profile.firstName}
                onChange={(e) => handleProfileFieldChange('firstName', e.target.value)}
                placeholder={PLACEHOLDERS.firstName}
                disabled={isSaving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                value={profile.lastName}
                onChange={(e) => handleProfileFieldChange('lastName', e.target.value)}
                placeholder={PLACEHOLDERS.lastName}
                disabled={isSaving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={profile.email}
                disabled
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                type="tel"
                id="phoneNumber"
                value={profile.phoneNumber}
                onChange={(e) => handleProfileFieldChange('phoneNumber', e.target.value)}
                placeholder={PLACEHOLDERS.phoneNumber}
                disabled={isSaving}
              />
            </div>

            {ADDRESS_FIELDS.map(({ key, label, placeholder }) => (
              <div className="form-group" key={key}>
                <label htmlFor={key}>{label}</label>
                <input
                  type="text"
                  id={key}
                  value={profile.address[key]}
                  onChange={(e) => handleAddressFieldChange(key, e.target.value)}
                  placeholder={placeholder}
                  disabled={isSaving}
                />
              </div>
            ))}

            <div className="form-group">
              <label htmlFor="fireDepartment">Fire Department</label>
              <FireDepartmentSelector
                inputId="fireDepartment"
                fireDepartments={fireDepartments}
                selectedFireDeptId={selectedFireDeptId}
                onChange={setSelectedFireDeptId}
                disabled={isSaving}
              />
            </div>

            <div className="form-group">
              <label>Notification Settings</label>
              <div className="notification-table">
                <div className="notification-table-header">
                  <span className="notification-topic-col">Topic</span>
                  {NOTIFICATION_CHANNELS.map((channel) => (
                    <span key={channel} className="notification-channel-col">
                      {CHANNEL_LABELS[channel]}
                    </span>
                  ))}
                </div>
                {notificationPreferences.map((preference) => (
                  <div key={preference.topic} className="notification-table-row">
                    <div className="notification-topic-col">
                      <span className="notification-topic-label">{preference.label}</span>
                      <p className="notification-topic-description">{preference.description}</p>
                    </div>
                    {NOTIFICATION_CHANNELS.map((channel) => (
                      <span key={channel} className="notification-channel-col">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={preference[channel]}
                            onChange={() => handleNotificationToggle(preference.topic, channel)}
                            disabled={isSaving}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <button type="submit" className="save-btn" disabled={isSaving}>
              {isSaving ? SAVING_BUTTON_LABEL : SAVE_BUTTON_LABEL}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Profile;
